import type { SemanticPortContext, StorefrontSemanticPorts } from './ports';

export interface StorefrontBffSemanticPortOptions {
  apiBaseUrl: string;
  tenantId?: string;
  startCheckout?: (input: {
    cartId?: string;
    mode?: 'cart' | 'buy_now';
  }, context: SemanticPortContext) => Promise<{ cartId: string }> | { cartId: string };
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function readLeadEmail(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value && typeof value === 'object') {
    const candidate = (value as { email?: unknown }).email;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
}

/**
 * Trusted Storefront BFF adapters shared by Layout and Checkout. Raw lead data
 * is sent only to the lead endpoint and is never returned in result telemetry.
 */
export function createStorefrontBffSemanticPorts(
  options: StorefrontBffSemanticPortOptions,
): StorefrontSemanticPorts {
  return {
    captureLead: async (input, context) => {
      const email = readLeadEmail(input.value);
      if (!email) {
        throw new Error('capture_lead requires a valid email value');
      }
      const response = await fetch(
        joinUrl(options.apiBaseUrl, '/v1/storefront/leads'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(options.tenantId
              ? { 'X-Tenant-ID': options.tenantId }
              : {}),
          },
          body: JSON.stringify({
            email,
            ...(input.formId ? { form_id: input.formId } : {}),
            ...(input.leadSource ? { lead_source: input.leadSource } : {}),
            ...(context.funnelId ? { funnel_id: context.funnelId } : {}),
            step_id: context.stepId,
          }),
          signal: context.signal,
        },
      );
      if (!response.ok) {
        throw new Error(`lead capture failed (${response.status})`);
      }
      const result = (await response.json()) as {
        accepted?: unknown;
        lead_ref?: unknown;
      };
      if (result.accepted !== true || typeof result.lead_ref !== 'string') {
        throw new Error('lead capture returned an invalid acknowledgement');
      }
      return {
        resultParams: {
          leadRef: result.lead_ref,
          ...(input.formId ? { formId: input.formId } : {}),
        },
      };
    },
    ...(options.startCheckout
      ? {
        startCheckout: async (input, context) => {
          const result = await options.startCheckout!(input, context);
          return {
            // Navigation is an intent boundary, not proof that Checkout
            // initialized the trusted cart. The destination owns the result.
            resultOwner: 'destination' as const,
            navigated: true,
            resultParams: {
              cartId: result.cartId,
              ...(input.mode ? { mode: input.mode } : {}),
            },
          };
        },
      }
      : {}),
  };
}
